import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { MdIcon } from '../Icon/MdIcon';
import './Dialog.css';

/**
 * A Material 3 dialog: scrim, extra-large shape, surface-container-high, a
 * headline, and a close icon button. From 600px down it becomes M3's
 * full-screen dialog, since a detail view that scrolls doesn't fit a basic
 * dialog on a phone.
 *
 * Accessibility: role="dialog" + aria-modal, labelled by the headline. Focus
 * moves to the close button on open, Tab cycles inside the dialog, and focus
 * goes back to whatever opened it on close. Escape and a scrim click close.
 *
 * Content may open its own overlay (the recording player is portalled to
 * <body>). Keys pressed in there are not ours, even though React bubbles them
 * up through the portal, so Escape closes only the topmost layer.
 */
export function Dialog({
  title,
  bare = false,
  labelledBy,
  onClose,
  children,
}: {
  title?: ReactNode;
  /**
   * Content draws its own header and close button (and names the dialog via
   * `labelledBy`). The dialog then adds only the scrim, shape, focus handling
   * and Escape. Used by call details, which keeps its own panel design.
   */
  bare?: boolean;
  labelledBy?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const ownTitleId = useId();
  const titleId = bare ? labelledBy : ownTitleId;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    (closeRef.current ?? panelRef.current?.querySelector('button'))?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    const panel = panelRef.current;
    if (!panel || !panel.contains(e.target as Node)) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="md-dialog__scrim"
      onMouseDown={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}>
      <div
        ref={panelRef}
        className={`md-dialog${bare ? ' md-dialog--bare' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}>
        {bare ? (
          children
        ) : (
          <>
            <header className="md-dialog__header">
              <h2 id={titleId} className="md-dialog__title">
                {title}
              </h2>
              <button
                ref={closeRef}
                type="button"
                className="md-icon-button"
                onClick={onClose}
                aria-label="Close">
                <MdIcon name="close" />
              </button>
            </header>
            <div className="md-dialog__content">{children}</div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
